import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { replace, type RouteObject, useNavigate, useRevalidator, useSearchParams } from 'react-router-dom'
import '../../kyc-explainer/kyc-explainer.css'

import { App } from '@capacitor/app'

// COMPONENTS
import * as Icons from '@icons'
import Button from '@components/atoms/button'
import Card from '@components/atoms/card'
import ThreeDots from '@components/atoms/three-dots'
import Spinner from '@/components/atoms/spinner'
import { Column } from '@components/templates/column'

// UTILITIES
import { PersonaAPI } from '@apis'
import { parseTeraUserData } from '@schemas'
import { createKycVerifiedLoader, useLoader } from '@utils/loaders'
import { platform } from '@utils/capacitor-helper'
import { getReferralId, clearReferralId } from '@utils/referral-storage'
import { getRequiredKycPhonePath } from '@utils/user-onboarding'


const KYCExplainerScreen: FC = () => {
    const navigate = useNavigate()
    const revalidator = useRevalidator()
    const [searchParams] = useSearchParams()

    const { status, id, sessionToken } = useLoader<typeof loader>()
    const fromParam = searchParams.get('from')
    const skipDestination = fromParam?.startsWith('/group/') ? fromParam : '/account'

    const spinnerRef = useRef<HTMLDialogElement>(null)
    useEffect(() => {
        if (spinnerRef.current) {
            if (revalidator.state === 'loading') spinnerRef.current.showModal()
            else spinnerRef.current.close()
        }
    }, [revalidator.state])

    useLayoutEffect(() => {
        const listener = App.addListener('resume', () => {
            console.log('[ App ] resume')
            revalidator.revalidate()
        })

        return () => {
            listener
                .then((handle) => handle.remove())
                .catch((error) => console.error('[ KycExplainer ] App.addListener(\'resume\')', error))
        }
    })

    const skipRef = useRef<HTMLDialogElement>(null)
    function openConfirmSkip() { if (skipRef.current) skipRef.current.showModal() }

    const [isCheckingReferral, setIsCheckingReferral] = useState(false)

    async function handleApprovedNext() {
        setIsCheckingReferral(true)
        spinnerRef.current?.showModal()

        try {
            const referralId = await getReferralId()
            if (referralId) await clearReferralId()
            navigate('/group')
        } catch (error) {
            console.error('Error handling referral redirect after KYC approval:', error)
            navigate('/group')
        } finally {
            setIsCheckingReferral(false)
            spinnerRef.current?.close()
        }
    }

    function navigateToVerify() {
        if (platform === 'web') {
            const verifyPath = fromParam ? `./verify?from=${encodeURIComponent(fromParam)}` : './verify'
            navigate(verifyPath)
            return
        }

        const url = sessionToken
            ? `https://splitcart.withpersona.com/verify?inquiry-id=${id}&session-token=${sessionToken}`
            : `https://splitcart.withpersona.com/verify?inquiry-id=${id}`

        window.open(url, '_blank')
    }

    return (
        <Column.Content>
            <div className="kyc-explainer-screen">
                {(status === 'pending' || status === 'created' || status === 'expired') && (<>
                    <h1>Identity Verification</h1>
                    <div className="info-banner">
                        <div className="bubble-sml flex items-center justify-center" style={{ background: 'var(--splitcard-white)', boxShadow: 'var(--box-shadow)', flexShrink: 0 }}>
                            <Icons.Lock tint="var(--splitcard-blue-600)" width="1.25rem" height="1.25rem" />
                        </div>
                        <div className='flex flex-col justify-start'>
                            <p className='text-body'><strong>Partnered with Persona</strong></p>
                            <p className='text-body-2'>A one-time, brief verification is required.</p>
                        </div>
                    </div>
                    <Card className="verification-steps-card light">
                        <div className="verification-steps-header">VERIFICATION STEPS</div>
                        <div className="verification-steps-content">
                            <div className="verification-step">
                                <div className="step-icon">
                                    <Icons.ID type="simple" tint="var(--splitcard-gray-600)" width="1.5rem" height="1.5rem" />
                                </div>
                                <p className="step-label text-body-2">Scan ID</p>
                            </div>
                            <div className="verification-step">
                                <div className="step-icon">
                                    <img src="/icons/smile.png" alt="Smile" style={{ width: '1.5rem', height: '1.5rem' }} />
                                </div>
                                <p className="step-label text-body-2">Selfie</p>
                            </div>
                            <div className="verification-step">
                                <div className="step-icon">
                                    <Icons.CheckSmall tint="var(--splitcard-gray-600)" width="1.5rem" height="1.5rem" />
                                </div>
                                <p className="step-label text-body-2">Verified</p>
                            </div>
                        </div>
                    </Card>
                    <p className='text-body-2 text-gray-500'>This helps us:</p>
                    <ul>
                        <li className="text-body">Protect your account from fraud & unauthorized access</li>
                        <li className="text-body">Prevent misuse of the platform</li>
                        <li className="text-body">Provide a real and trusted Splitcard community</li>
                    </ul>
                </>)}
                {(status === 'needs_review') && (<>
                    <div className="bubble-lrg flex items-center justify-center" style={{ alignSelf: 'center', background: 'var(--splitcard-yellow-500)', boxShadow: 'var(--box-shadow)' }}>
                        <Icons.Clock tint="var(--splitcard-white)" width="5em" height="5em" />
                    </div>
                    <h1>Sign-up In Review</h1>
                    <p className='text-body'>To ensure the security of our users and platform, your account is currently under review.</p>
                    <ThreeDots color='var(--splitcard-yellow-500)' />
                </>)}
                {(status === 'approved' || status === 'completed') && (<>
                    <div className="bubble-lrg flex items-center justify-center" style={{ alignSelf: 'center', background: 'var(--splitcard-green-600)', boxShadow: 'var(--box-shadow)' }}>
                        <Icons.CheckSmall tint="var(--splitcard-white)" width="5em" height="5em" />
                    </div>
                    <h1>You're Approved!</h1>
                    <p className='text-body'>Your card has been issued, and you can start using it now.</p>
                    <ThreeDots color='var(--splitcard-green-600)' />
                </>)}
                {(status === 'failed' || status === 'declined') && (<>
                    <div className="bubble-lrg flex items-center justify-center" style={{ alignSelf: 'center', background: 'var(--splitcard-red-400)', boxShadow: 'var(--box-shadow)' }}>
                        <Icons.Close tint="var(--splitcard-white)" width="5em" height="5em" />
                    </div>
                    <h1>Identity Check Failed</h1>
                    <p className='text-body'>Please contact support for further assistance.</p>
                    <ThreeDots color='var(--splitcard-red-400)' />
                    <Card style={{ marginTop: 'auto', textAlign: 'center' }}>
                        <p className='body2'><strong>Contact Support</strong></p>
                        <div className='flex flex-row items-center justify-center gap-2'>
                            <Icons.Phone tint='var(--splitcard-blue-500)' height={'1rem'} width={'1rem'} />
                            <a onClick={() => window.open('tel:+18886254349')}>888-625-4349</a>
                        </div>
                        <div className='flex flex-row items-center justify-center gap-2'>
                            <Icons.Email tint='var(--splitcard-blue-500)' height={'1rem'} width={'1rem'} />
                            <a onClick={() => window.open('mailto:support@splitcard.com')}>support@splitcard.com</a>
                        </div>
                    </Card>
                </>)}
            </div>
            <Column.Footer>
                {(status === 'pending' || status === 'created' || status === 'expired') && (<>
                    <Button theme="light" onClick={openConfirmSkip}>Skip For Now</Button>
                    <Button style={{ whiteSpace: 'nowrap' }} theme="primary" onClick={navigateToVerify}>
                        Verify Now
                    </Button>
                </>)}
                {(status === 'needs_review') && <Button theme="primary" to="/account">Go Back</Button>}
                {(status === 'approved' || status === 'completed') && (
                    <Button style={{ whiteSpace: 'nowrap' }} theme="primary" onClick={handleApprovedNext} disabled={isCheckingReferral}>Create Group</Button>
                )}
            </Column.Footer>
            <dialog ref={spinnerRef}>
                <div>
                    <div className="flex flex-col items-center justify-center" style={{ background: 'var(--splitcard-white)', gap: '1rem', padding: '1rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <Spinner />
                    </div>
                </div>
            </dialog>
            <dialog ref={skipRef}>
                <div>
                    <div className="flex flex-col items-stretch" style={{ maxWidth: 'calc(var(--max-content-width, 610px) - 8rem)', textAlign: 'center', margin: '1rem', background: 'var(--splitcard-white)', gap: '1rem', padding: '1.3125rem', borderRadius: 'var(--border-radius)', overflowY: 'auto' }}>
                        <h2>Skip Verification?</h2>
                        <p>Verification must be completed before you create your own group. You can still join groups you are invited to in the meantime.</p>
                        <div className="flex flex-col items-stretch gap-2.5">
                            <Button theme="primary" style={{ whiteSpace: 'nowrap' }} onClick={navigateToVerify}>Complete Verification</Button>
                            <Button theme="light" to={skipDestination}>Skip For Now</Button>
                        </div>
                    </div>
                </div>
            </dialog>
        </Column.Content>
    )
}

const loader = createKycVerifiedLoader(async ({ request }) => {
    const search = new URL(request.url).search
    const tera = parseTeraUserData(window.Clerk?.user?.publicMetadata.tera)
    if (!tera.address) return replace(`/sign-up/billing${search}`)

    const phonePath = getRequiredKycPhonePath(window.Clerk?.user, search)
    if (phonePath) return replace(phonePath)

    return await PersonaAPI.CreateInquiryRequest.fetch(window.Clerk?.user?.id)
})

const KycExplainerRoute: RouteObject = {
    index: true,
    element: <KYCExplainerScreen />,
    loader,
}

export { KycExplainerRoute }
